/**
 * Shared layout for legal pages (Privacy + Terms). Keeps formatting consistent
 * and easy to extract section bodies as separate React children.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, MessageSquare } from 'lucide-react';
import { ATLAS_LOGO_URL, ATLAS_NAME } from '@/lib/brand';

export default function LegalPageLayout({ title, lastUpdated, children }) {
  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#2D3748]">
      <nav className="sticky top-0 z-40 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-[#EBE7E0]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2.5 min-w-0" data-testid="legal-brand-link">
            <div className="w-9 h-9 rounded-md bg-white border border-[#EBE7E0] flex items-center justify-center overflow-hidden flex-shrink-0">
              <img src={ATLAS_LOGO_URL} alt="" className="w-full h-full object-contain" />
            </div>
            <span className="font-serif-display text-base text-[#14213D] truncate">{ATLAS_NAME}</span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
            <Link to="/contact" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[#5A6577] hover:text-[#14213D]" data-testid="legal-contact-link">
              <MessageSquare className="h-4 w-4" /> Contact
            </Link>
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#5A6577] hover:text-[#14213D]" data-testid="legal-home-link">
              <ArrowLeft className="h-4 w-4" /> Home
            </Link>
          </div>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">Legal</p>
        <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[#14213D] tracking-tight mb-3 leading-tight">
          {title}
        </h1>
        <p className="text-sm text-[#5A6577] mb-10">Last updated: {lastUpdated}</p>

        <div className="prose prose-sm sm:prose max-w-none text-[#2D3748] leading-relaxed break-words">
          {children}
        </div>

        <div className="mt-16 pt-8 border-t border-[#EBE7E0] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-[#5A6577]">
          <span>Questions? Reach out:</span>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/contact" className="inline-flex items-center gap-1.5 text-[#14213D] hover:text-[#1D2D50]" data-testid="legal-contact-form-link">
              <MessageSquare className="h-4 w-4" /> Contact form
            </Link>
            <a href="mailto:support@atlashistory.org" className="inline-flex items-center gap-1.5 text-[#14213D] hover:text-[#1D2D50] break-all" data-testid="legal-contact-email">
              <Mail className="h-4 w-4" /> support@atlashistory.org
            </a>
          </div>
        </div>
      </article>
    </div>
  );
}
